import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogDescription, DialogTitle } from "@jobenvoy/ui";
import { useAuth } from "@/auth/session";
import {
    BellIcon,
    BookmarkIcon,
    CloseIcon,
    GlobeIcon,
    MarkIcon,
    SparkIcon,
    UploadIcon,
} from "@/components/icons";
import TermsCheck from "@/components/consent/TermsCheck";
import { keepSignupAcceptance } from "@/lib/consent";
import { actionProps, scopeProps } from "@/lib/controlAnalytics";
import { track, trackControl } from "@/lib/analytics";
import {
    dropStaleIntent,
    GATE_COPY,
    keepIntent,
    screenFor,
    TAILORS_USED,
    type GateKind,
} from "@/lib/gate";
import { usePlans } from "@/lib/hooks/billing";
import { useStanding } from "@/lib/hooks/standingContext";
import { packAsk, upgradeCards } from "@/lib/pricing";
import { ROUTES } from "@/lib/routes";
import { offersPlans } from "@/lib/standing";
import type { ToolJob } from "@/lib/tools/pastedJob";
import { GateContext } from "./gateContext";
import PlaceLink from "./PlaceLink";
import { GRAD_BUTTON, OUTLINE_BUTTON } from "./styles";

interface Asked {
    kind: GateKind;
    postingId?: string;
    pasted?: ToolJob;
}

function sessionStore(): Storage | undefined {
    try {
        return window.sessionStorage;
    } catch {
        return undefined;
    }
}

/**
 * The one sheet a gated action opens, named for the action, and the way
 * back: the sign-in returns to the page the person was on and does what
 * they pressed (`IntentOnReturn`).
 */
export function GateProvider({ children }: { children: ReactNode }) {
    const [asked, setAsked] = useState<Asked | null>(null);
    // A sign-in that was never finished leaves nothing kept past its time.
    useEffect(() => dropStaleIntent(sessionStore()), []);
    const ask = useCallback((kind: GateKind, postingId?: string, pasted?: ToolJob) => {
        trackControl("join_dialog_opened", { source: "Gate", section: kind });
        setAsked({ kind, postingId, pasted });
    }, []);
    const gate = useMemo(() => ({ ask }), [ask]);
    return (
        <GateContext.Provider value={gate}>
            {children}
            <GateSheet
                asked={asked}
                onClose={() => setAsked(null)}
                onSwitch={(kind) =>
                    setAsked((current) => ({ kind, postingId: current?.postingId }))
                }
            />
        </GateContext.Provider>
    );
}

const ICON: Record<GateKind, ReactNode> = {
    save: <BookmarkIcon className="size-[22px]" />,
    tailor: <SparkIcon className="size-[22px]" />,
    tool_tailor: <SparkIcon className="size-[22px]" />,
    alerts: <BellIcon className="size-[22px]" />,
    join: <MarkIcon className="size-[22px]" />,
    signin: <MarkIcon className="size-[22px]" />,
    upload: <UploadIcon className="size-[22px]" />,
    envoy: <MarkIcon className="size-[22px]" />,
    upgrade: <SparkIcon className="size-[22px]" />,
};

/**
 * The paid plans side by side, See the plans and a pack: the one ask to
 * upgrade. Plans and packs are billing's.
 */
function UpgradeChoices({
    showPlans,
    onNavigate,
}: {
    /** Each card names a plan's tailors a month, so a paid member gets none of them. */
    showPlans: boolean;
    onNavigate: () => void;
}) {
    const plans = usePlans().data;
    const cards = showPlans ? upgradeCards(plans) : [];
    const pack = packAsk(plans);
    return (
        <div className="flex flex-col gap-2">
            {cards.length > 0 && (
                <div className="grid grid-cols-2 gap-2 max-[380px]:grid-cols-1">
                    {cards.map((plan) => (
                        <PlaceLink
                            {...actionProps("upgrade_plan_clicked", {
                                source: "Gate.UpgradeChoices",
                                plan: plan.tier,
                            })}
                            key={plan.tier}
                            to={ROUTES.PRICING}
                            onClick={onNavigate}
                            className={`flex flex-col gap-1 rounded-xl px-3.5 py-3 text-fg transition-transform duration-200 ease-[cubic-bezier(.2,.9,.25,1.15)] hover:-translate-y-0.5 ${
                                plan.lead ? "gradient-frame" : "border border-line bg-card"
                            }`}
                        >
                            <span className="flex items-center justify-between gap-2">
                                <span className="font-display text-[17px] font-bold">
                                    {plan.name}
                                </span>
                                <span className="font-mono text-[12.5px] font-semibold">
                                    {plan.price}
                                </span>
                            </span>
                            <span className="text-[12.5px] text-fg-muted">{plan.line}</span>
                        </PlaceLink>
                    ))}
                </div>
            )}
            <PlaceLink
                {...actionProps("plan_upgrade_clicked", { source: "Gate.UpgradeChoices" })}
                to={ROUTES.PRICING}
                onClick={onNavigate}
                className={`${GRAD_BUTTON} h-11 rounded-control text-[14px]`}
            >
                See the plans
            </PlaceLink>
            {pack && (
                <PlaceLink
                    {...actionProps("upgrade_pack_clicked", { source: "Gate.UpgradeChoices" })}
                    to={`${ROUTES.PRICING}#packs`}
                    onClick={onNavigate}
                    className={`${OUTLINE_BUTTON} h-11 rounded-control text-[14px]`}
                >
                    {pack}
                </PlaceLink>
            )}
            <p className="m-0 text-center text-[12.5px] text-fg-faint">
                Saving and searching never spend a tailor.
            </p>
        </div>
    );
}

/** What an unticked box says when Sign up is pressed. */
export const AGREE_FIRST = "Tick the box to agree before you sign up.";

function GateSheet({
    asked,
    onClose,
    onSwitch,
}: {
    asked: Asked | null;
    onClose: () => void;
    onSwitch: (kind: GateKind) => void;
}) {
    const { signIn, signUp } = useAuth();
    const { pathname, search } = useLocation();
    const showPlans = offersPlans(useStanding());
    // The last sheet asked for stays drawn while the sheet closes.
    const [shown, setShown] = useState<Asked | null>(asked);
    // Every sheet opens with the box unticked: a tick is for one sign up.
    const [agreed, setAgreed] = useState(false);
    const [agreeError, setAgreeError] = useState<string | null>(null);
    if (asked && asked !== shown) {
        setShown(asked);
        setAgreed(false);
        setAgreeError(null);
    }
    const kind = (asked ?? shown)?.kind ?? "join";
    const copy = GATE_COPY[kind];
    const line = kind === "upgrade" && !showPlans ? TAILORS_USED : copy.line;
    const signsUp = screenFor(kind) === "sign-up";

    const go = (screen: "sign-in" | "sign-up") => {
        if (screen === "sign-up") {
            // Signing up is agreeing: not without the box ticked, and the
            // tick is kept to be recorded once the sign-in comes back.
            if (!agreed) {
                setAgreeError(AGREE_FIRST);
                document.getElementById("gate-terms")?.focus();
                return;
            }
            keepSignupAcceptance(sessionStore());
        }
        keepIntent(sessionStore(), {
            kind,
            path: `${pathname}${search}`,
            ...(asked?.postingId ? { postingId: asked.postingId } : {}),
            ...(asked?.pasted ? { pasted: asked.pasted } : {}),
        });
        track({ name: "sign_in_opened", screen, via: screen === "sign-up" ? "google" : "link" });
        if (screen === "sign-up") signUp();
        else signIn();
    };

    return (
        <Dialog
            {...scopeProps({ source: "Gate", section: kind })}
            open={asked !== null}
            onClose={() => {
                trackControl("dialog_dismissed", { source: "Gate", section: kind });
                onClose();
            }}
            className="relative rounded-[22px] border border-line p-6 shadow-dialog duration-[260ms] ease-[cubic-bezier(.2,.9,.25,1.12)] max-[760px]:rounded-b-none max-[760px]:pb-[max(24px,env(safe-area-inset-bottom))] max-[760px]:data-closed:translate-y-full"
            backdropClassName="bg-scrim backdrop-blur-[3px]"
            frameClassName="max-[760px]:!p-0"
            rowClassName="max-[760px]:items-end"
            panelStyle={{ maxWidth: 420 }}
        >
            <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3.5 pr-8">
                    <span
                        className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-grad text-brand-fg"
                        aria-hidden="true"
                    >
                        {ICON[kind]}
                    </span>
                    <div className="flex flex-col gap-1">
                        <DialogTitle className="m-0 font-display text-[21px] font-bold leading-tight tracking-[-0.02em] sm:text-[21px]">
                            {copy.title}
                        </DialogTitle>
                        <DialogDescription className="m-0 text-[13.5px] leading-[1.45] text-fg-muted">
                            {line}
                        </DialogDescription>
                    </div>
                </div>
                {kind === "upgrade" ? (
                    <UpgradeChoices showPlans={showPlans} onNavigate={onClose} />
                ) : (
                    <>
                        {signsUp && (
                            <TermsCheck
                                id="gate-terms"
                                checked={agreed}
                                error={agreeError}
                                action="signup_terms_toggled"
                                source="Gate"
                                onChange={(next) => {
                                    setAgreed(next);
                                    if (next) setAgreeError(null);
                                }}
                            />
                        )}
                        <button
                            {...actionProps("gate_continue_clicked", {
                                source: "Gate",
                                section: kind,
                                mode: screenFor(kind),
                            })}
                            type="button"
                            onClick={() => go(screenFor(kind))}
                            className={`${GRAD_BUTTON} h-11 gap-2.5 rounded-control text-[14px]`}
                        >
                            <GlobeIcon className="size-4" />
                            {/* Named for where it goes: a visitor's sheet signs up. */}
                            {screenFor(kind) === "sign-in" ? "Sign in" : "Sign up"}
                        </button>
                        <p className="m-0 text-center text-[12.5px] leading-normal text-fg-faint">
                            {kind === "signin"
                                ? "Signing in is handled by WorkOS; Job Envoy never sees a password. New here? "
                                : "Free. No card. Signing in is handled by WorkOS; Job Envoy never sees a password. Already have an account? "}
                            <button
                                {...actionProps("gate_signin_clicked", {
                                    source: "Gate",
                                    section: kind,
                                })}
                                type="button"
                                // A new account starts on the sheet with the box to tick.
                                onClick={() =>
                                    kind === "signin" ? onSwitch("join") : go("sign-in")
                                }
                                className="border-0 bg-transparent p-0 text-[12.5px] font-semibold text-brand-ink"
                            >
                                {kind === "signin" ? "Create free account" : "Sign in"}
                            </button>
                        </p>
                    </>
                )}
            </div>
            <button
                {...actionProps("gate_sheet_closed", { source: "Gate", section: kind })}
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute right-3 top-3 min-[761px]:right-4 min-[761px]:top-4 grid size-11 min-[761px]:size-8 place-items-center rounded-[9px] text-fg-muted transition-colors hover:bg-hover"
            >
                <CloseIcon className="size-5 min-[761px]:size-4" />
            </button>
        </Dialog>
    );
}
